/** Admin Staff — API mappers & listing config. */

import {
  formatDisplayName,
  getUserFullName,
} from "../../../helpers/userDisplay"

export const STAFF_STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "inactive", label: "Inactive" },
]

export const STAFF_SORT_OPTIONS = [
  { value: "first_name", label: "First Name" },
  { value: "last_name", label: "Last Name" },
  { value: "email", label: "Email" },
  { value: "type", label: "Type" },
  { value: "created_at", label: "Created At" },
  { value: "updated_at", label: "Updated At" },
]

export const DEFAULT_STAFF_SORT_BY = "first_name"
export const DEFAULT_STAFF_SORT_DIRECTION = "asc"

export const STAFF_LIST_WITH = ["roles.permissions", "currentRoles", "cities"]

export const STAFF_DETAIL_WITH = ["roles.permissions", "currentRoles", "cities"]

export const getStaffInitials = name =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map(part => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase()

const normalizeStaffToneKey = value =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")

/** Role pill tones — outlined chips on Staff listing (Figma). */
export const STAFF_ROLE_PILL_TONES = {
  admin: "admin",
  "super-admin": "admin",
  support: "support",
  "city-admin": "city-admin",
  "finance-manager": "finance",
  finance: "finance",
  "content-moderator": "moderator",
  moderator: "moderator",
}

const STAFF_ROLE_TONE_FALLBACKS = [
  "admin",
  "support",
  "city-admin",
  "finance",
  "moderator",
]

export const resolveStaffRolePillTone = (roleName = "") => {
  const key = normalizeStaffToneKey(roleName)
  if (!key) return "support"
  if (STAFF_ROLE_PILL_TONES[key]) return STAFF_ROLE_PILL_TONES[key]

  let hash = 0
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash + key.charCodeAt(i) * (i + 1)) % STAFF_ROLE_TONE_FALLBACKS.length
  }
  return STAFF_ROLE_TONE_FALLBACKS[hash]
}
const formatCount = value => {
  const num = Number(value)
  if (!Number.isFinite(num)) return "--"
  return num.toLocaleString("en-US")
}

/** Map GET /api/v1/admin/staff/summary → KPI cards. */
export const mapStaffSummaryKpi = (result = {}) => [
  {
    key: "total",
    title: "Total Staff",
    value: formatCount(result.total_staff),
    tone: "lightblue-custom",
  },
  {
    key: "active",
    title: "Active",
    value: formatCount(result.active_staff),
    tone: "lightteal",
  },
  {
    key: "inactive",
    title: "Inactive",
    value: formatCount(result.inactive_staff),
    tone: "red",
  },
]

const pickCityIds = item => {
  if (Array.isArray(item.cities) && item.cities.length > 0) {
    return item.cities.map(city => city.id).filter(Boolean)
  }
  if (Array.isArray(item.city_ids) && item.city_ids.length > 0) {
    return item.city_ids.filter(Boolean)
  }
  return []
}

const formatStaffCityLabel = city => {
  if (!city) return ""
  if (typeof city === "string") return city.trim()

  return String(city.city || city.name || "").trim()
}

const pickCityLabels = item => {
  if (Array.isArray(item.cities) && item.cities.length > 0) {
    return item.cities.map(formatStaffCityLabel).filter(Boolean)
  }
  return []
}

const pickStaffRoles = item => {
  const source = [
    ...(Array.isArray(item.current_roles) ? item.current_roles : []),
    ...(Array.isArray(item.currentRoles) ? item.currentRoles : []),
    ...(Array.isArray(item.roles) ? item.roles : []),
  ]

  const seen = new Set()
  return source
    .map(entry => {
      const nested =
        entry?.role && typeof entry.role === "object" ? entry.role : null
      const id = String(
        nested?.id || entry?.role_id || entry?.id || "",
      ).trim()
      const name = String(
        nested?.name ||
          nested?.public_name ||
          entry?.name ||
          entry?.public_name ||
          "",
      ).trim()
      if (!id || seen.has(id)) return null
      seen.add(id)
      return { id, name }
    })
    .filter(Boolean)
}

/**
 * Map staff list/detail item → table / profile row.
 */
export const mapStaffRow = item => {
  const firstName = formatDisplayName(item.first_name || "")
  const lastName = formatDisplayName(item.last_name || "")
  const name =
    getUserFullName({
      full_name: item.full_name,
      first_name: firstName,
      last_name: lastName,
      email: item.email,
    }) || "--"
  const roles = pickStaffRoles(item)
  const primaryRole = roles[0]
  const isActive = item.is_active !== false

  return {
    id: item.id,
    name,
    firstName,
    lastName,
    email: item.email || "--",
    phone: item.phone_number || "",
    notes: item.bio || "",
    role: primaryRole?.name || "--",
    roleId: primaryRole?.id || "",
    roleIds: roles.map(role => role.id).filter(Boolean),
    roleLabels: roles.map(role => role.name).filter(Boolean),
    cityIds: pickCityIds(item),
    cityLabels: pickCityLabels(item),
    citiesLabel: pickCityLabels(item).join(", ") || "--",
    status: isActive ? "active" : "inactive",
    isActive,
    type: item.type || "",
    joinedDate: item.created_at || "",
    image:
      item.profile_media?.thumbnail ||
      item.profile_media?.image ||
      item.thumbnail ||
      item.image ||
      "",
    raw: item,
  }
}

export const mapRoleSelectOptions = (items = []) =>
  (Array.isArray(items) ? items : []).map(item => ({
    value: item.id,
    label: item.name || item.id,
  }))

export const mapCitySelectOptions = (items = []) =>
  (Array.isArray(items) ? items : []).map(item => ({
    value: item.id,
    label: item.city || item.name || item.id,
  }))

const appendArrayField = (formData, key, values = []) => {
  ;(Array.isArray(values) ? values : []).forEach(value => {
    if (value == null || value === "") return
    formData.append(`${key}[]`, String(value))
  })
}

/**
 * Build multipart payload for staff create/update.
 * Create: POST /api/v1/admin/staff/create
 * Update: POST /api/v1/admin/staff/update/:id
 * Notes only on edit (API field: `bio`); `profile_image` when a new file is chosen.
 */
export const buildStaffFormData = ({
  firstName,
  lastName,
  email,
  password,
  passwordConfirmation,
  roleId,
  roleIds,
  cityIds,
  notes,
  profileImage,
  includeNotes = false,
  includeEmail = false,
  includePassword = false,
} = {}) => {
  const formData = new FormData()

  formData.append("first_name", String(firstName || "").trim())
  formData.append("last_name", String(lastName || "").trim())

  if (includeEmail) {
    formData.append("email", String(email || "").trim())
  }

  if (includePassword) {
    formData.append("password", String(password || ""))
    formData.append(
      "password_confirmation",
      String(passwordConfirmation || ""),
    )
  }

  const roles = roleId
    ? [roleId]
    : Array.isArray(roleIds)
      ? roleIds
      : []
  appendArrayField(formData, "role_ids", roles)
  appendArrayField(formData, "city_ids", cityIds)

  if (includeNotes) {
    // Staff detail API exposes this as `bio`
    formData.append("bio", String(notes || "").trim())
  }

  if (profileImage instanceof File || profileImage instanceof Blob) {
    formData.append("profile_image", profileImage)
  }

  return formData
}

const humanizeKey = value =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase())

const formatRelativeTime = value => {
  if (!value) return "--"
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return "--"
  const diffMs = Date.now() - parsed.getTime()
  const minutes = Math.floor(diffMs / 60000)
  if (minutes < 1) return "Just now"
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

const inferStaffActivityIcon = log => {
  const haystack = [
    log?.log_name,
    log?.description,
    log?.subject_type,
    log?.properties?.event,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()

  if (/flag|report|safety/.test(haystack)) return "security"
  if (/call for|inbox|search/.test(haystack)) return "search"
  if (/photo|camera|image/.test(haystack)) return "camera"
  if (/city|transfer|map/.test(haystack)) return "map"
  if (/reward|xp|award|review/.test(haystack)) return "security"
  return "user"
}

/**
 * Map GET /api/v1/admin/activity-logs?user_id= → Staff Profile Recent Activities.
 */
export const mapStaffRecentActivities = (result, limit = 5) => {
  const rows = Array.isArray(result)
    ? result
    : Array.isArray(result?.data)
      ? result.data
      : []

  return rows.slice(0, limit).map((log, index) => {
    const properties = log?.properties || {}
    const description =
      log?.description || humanizeKey(log?.log_name) || "Activity"
    const actor =
      properties.causer_name ||
      properties.user_name ||
      properties.actor_name ||
      ""

    return {
      id: log.id ?? `staff-activity-${index}`,
      title: actor ? `${actor} — ${description}` : description,
      subtitle:
        humanizeKey(log?.subject_type) ||
        humanizeKey(properties.event) ||
        "Staff activity",
      time: formatRelativeTime(log.created_at),
      iconKey: inferStaffActivityIcon(log),
    }
  })
}
